import { useDispatch } from 'react-redux';
import axiosInstanceQueries from '../queriesAxios';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import app from 'store/app/app';

export interface CustomProfile {
  customProfileString?: string;
  customProfileName: string;
  orgId: string;
  fileName?: string;
  id: string;
  profileType?: 'graphic' | 'barcode';
  rasterDPI?: number;
  barcodeReRasterDpi?: number;
  createdAt?: string;
  updatedAt?: string;
}
export interface CreateCustomProfile {
  customProfileString: string;
  customProfileName: string;
  orgId: string;
  fileName?: string;
  profileType?: 'graphic' | 'barcode';
  rasterDPI?: number;
  barcodeReRasterDpi?: number;
}
async function deleteCustomProfile(profileId: string): Promise<void> {
  await axiosInstanceQueries.delete(`/organizations/custom-profile/${profileId}`);
}

async function copyCustomProfile(profileId: string, targetOrgId: string, newProfileName: string): Promise<void> {
  await axiosInstanceQueries.post(`/organizations/custom-profile/${profileId}/copy`, { targetOrgId, newProfileName });
}

async function updateCustomProfile(profile: CustomProfile): Promise<void> {
  await axiosInstanceQueries.patch(`/organizations/custom-profile/${profile.id}`, {
    customProfileName: profile.customProfileName,
    customProfileString: profile.customProfileString,
    orgId: profile.orgId,
    fileName: profile.fileName,
    profileType: profile.profileType,
    rasterDPI: profile.rasterDPI,
    barcodeReRasterDpi: profile.barcodeReRasterDpi,
  });
}

async function fetchCustomProfiles(orgId?: string, additionalProfileId?: string): Promise<any> {
  if (orgId) {
    return axiosInstanceQueries.get(`/organizations/custom-profile/${orgId}`).then((response) => response.data);
  }

  const params = {
    ...(additionalProfileId && { additionalProfileId }),
  };

  return axiosInstanceQueries.get('/organizations/custom-profile', { params }).then((response) => response.data);
}

export async function fetchCustomProfileById(profileId: string): Promise<CustomProfile> {
  return axiosInstanceQueries.get(`/organizations/custom-profile-by-id/${profileId}`).then((response) => response.data);
}

export const useCustomProfiles = (orgId?: string, additionalProfileId?: string) => {
  const queryInfo = useQuery({
    queryKey: ['customProfiles', orgId, additionalProfileId],
    queryFn: () => fetchCustomProfiles(orgId, additionalProfileId),
    staleTime: 1000 * 60 * 5,
  });
  return {
    ...queryInfo,
    customProfilesList: queryInfo.data || [],
  };
};

export const useCustomProfileById = (profileId?: string) => {
  return useQuery({
    queryKey: ['customProfile', profileId],
    queryFn: () => fetchCustomProfileById(profileId!),
    enabled: !!profileId,
    staleTime: 30000, // Refetch every 30 seconds to balance freshness and efficiency
    refetchOnMount: true,
  });
};
async function uploadCustomProfile({
  customProfileString,
  customProfileName,
  orgId,
  fileName,
  profileType,
  rasterDPI,
  barcodeReRasterDpi,
}: CreateCustomProfile): Promise<{ id: string }> {
  const response = await axiosInstanceQueries.post('/organizations/custom-profile', {
    customProfileString,
    customProfileName,
    orgId,
    fileName,
    profileType,
    rasterDPI,
    barcodeReRasterDpi,
  });
  return response.data;
}

export const useDeleteCustomProfile = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (profileId) => deleteCustomProfile(profileId),
    onSuccess: (data, profileId) => {
      // Invalidate all customProfiles queries (since we don't have orgId)
      queryClient.invalidateQueries({ queryKey: ['customProfiles'] });
      queryClient.invalidateQueries({ queryKey: ['customProfile', profileId] });
      dispatch(app.actions.setSnackMessage({ message: 'Profile deleted successfully!', type: 'success' }));
    },
    onError: (error) => {
      dispatch(app.actions.setSnackMessage({ message: `Error deleting profile: ${error.message}`, type: 'error' }));
    },
  });
};

export const useCopyCustomProfile = (): UseMutationResult<
  void,
  Error,
  { profileId: string; targetOrgId: string; newProfileName: string }
> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ profileId, targetOrgId, newProfileName }) =>
      copyCustomProfile(profileId, targetOrgId, newProfileName),
    onSuccess: () => {
      // Invalidate all customProfiles queries to refresh both source and target org lists
      queryClient.invalidateQueries({ queryKey: ['customProfiles'] });
      dispatch(app.actions.setSnackMessage({ message: 'Profile copied successfully!', type: 'success' }));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to copy profile';
      dispatch(app.actions.setSnackMessage({ message: errorMessage, type: 'error' }));
    },
  });
};

export const useUploadCustomProfile = (): UseMutationResult<{ id: string }, Error, CreateCustomProfile> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (profileData) => uploadCustomProfile(profileData),
    onMutate: async (newProfile) => {
      // Cancel outgoing refetches for this specific orgId (with undefined additionalProfileId)
      await queryClient.cancelQueries({ queryKey: ['customProfiles', newProfile.orgId, undefined] });

      // Snapshot previous value
      const previousProfiles = queryClient.getQueryData(['customProfiles', newProfile.orgId, undefined]);

      // Optimistically add the new profile to the list (with temporary ID)
      queryClient.setQueryData(['customProfiles', newProfile.orgId, undefined], (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        const tempProfile: CustomProfile = {
          id: `temp-${Date.now()}`,
          customProfileName: newProfile.customProfileName,
          orgId: newProfile.orgId,
          fileName: newProfile.fileName,
          profileType: newProfile.profileType,
        };
        return [...old, tempProfile];
      });

      return { previousProfiles, orgId: newProfile.orgId };
    },
    onError: (error, newProfile, context: any) => {
      // Rollback on error
      if (context?.previousProfiles && context?.orgId) {
        queryClient.setQueryData(['customProfiles', context.orgId, undefined], context.previousProfiles);
      }
      dispatch(app.actions.setSnackMessage({ message: `Error uploading profile: ${error.message}`, type: 'error' }));
    },
    onSuccess: () => {
      dispatch(app.actions.setSnackMessage({ message: 'Profile uploaded successfully!', type: 'success' }));
    },
    onSettled: (data, error, variables) => {
      // Refetch to get the real ID and latest data from server
      queryClient.invalidateQueries({ queryKey: ['customProfiles', variables.orgId, undefined] });
    },
  });
};

export const useUpdateCustomProfile = (): UseMutationResult<void, Error, CustomProfile> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (profile) => updateCustomProfile(profile),
    onMutate: async (newProfile) => {
      // Cancel outgoing refetches for Settings (orgId key) and Prep dropdowns (undefined orgId key)
      await queryClient.cancelQueries({ queryKey: ['customProfiles', newProfile.orgId, undefined] });
      await queryClient.cancelQueries({ queryKey: ['customProfiles', undefined] });
      await queryClient.cancelQueries({ queryKey: ['customProfile', newProfile.id] });

      // Snapshot previous values
      const previousProfiles = queryClient.getQueryData(['customProfiles', newProfile.orgId, undefined]);
      const previousProfilesUndefined = queryClient.getQueryData(['customProfiles', undefined]);
      const previousProfile = queryClient.getQueryData(['customProfile', newProfile.id]);

      // Optimistically update to the new value
      queryClient.setQueryData(['customProfile', newProfile.id], newProfile);

      const applyOptimisticUpdate = (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((profile: CustomProfile) =>
          profile.id === newProfile.id
            ? { ...profile, customProfileName: newProfile.customProfileName, profileType: newProfile.profileType }
            : profile,
        );
      };

      queryClient.setQueryData(['customProfiles', newProfile.orgId, undefined], applyOptimisticUpdate);
      queryClient.setQueryData(['customProfiles', undefined], applyOptimisticUpdate);

      return { previousProfiles, previousProfilesUndefined, previousProfile, orgId: newProfile.orgId };
    },
    onError: (error, newProfile, context: any) => {
      // Rollback on error
      if (context?.previousProfiles && context?.orgId) {
        queryClient.setQueryData(['customProfiles', context.orgId, undefined], context.previousProfiles);
      }
      if (context?.previousProfilesUndefined) {
        queryClient.setQueryData(['customProfiles', undefined], context.previousProfilesUndefined);
      }
      if (context?.previousProfile) {
        queryClient.setQueryData(['customProfile', newProfile.id], context.previousProfile);
      }
      dispatch(app.actions.setSnackMessage({ message: `Error updating profile: ${error.message}`, type: 'error' }));
    },
    onSuccess: () => {
      dispatch(app.actions.setSnackMessage({ message: 'Profile updated successfully!', type: 'success' }));
    },
    onSettled: (data, error, variables) => {
      // Invalidate by orgId (Settings page) and by undefined orgId (Prep dropdowns)
      queryClient.invalidateQueries({ queryKey: ['customProfiles', variables.orgId, undefined] });
      queryClient.invalidateQueries({ queryKey: ['customProfiles', undefined] });
      queryClient.invalidateQueries({ queryKey: ['customProfile', variables.id] });
    },
  });
};
